// Hangul syllable ↔ jamo, plus the little bit of phonology the "Sound it out"
// drill needs. A precomposed syllable (U+AC00…U+D7A3) is
//     0xAC00 + (initial × 21 + vowel) × 28 + final
// so taking a block apart is arithmetic — no font, no dictionary. The letters
// (jamo) are the sound units; strokes inside a letter have no sound of their
// own, which is why the drill lights up jamo and not strokes.

export const INITIALS = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ']
export const VOWELS = ['ㅏ', 'ㅐ', 'ㅑ', 'ㅒ', 'ㅓ', 'ㅔ', 'ㅕ', 'ㅖ', 'ㅗ', 'ㅘ', 'ㅙ', 'ㅚ', 'ㅛ', 'ㅜ', 'ㅝ', 'ㅞ', 'ㅟ', 'ㅠ', 'ㅡ', 'ㅢ', 'ㅣ']
export const FINALS = ['', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ', 'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ']

// Revised Romanization, per letter and per role. A final is pronounced as one
// of seven sounds (k n t l m p ng) whatever letter spells it, which is also why
// finals are scored by sound class rather than by letter.
export const ROM_INITIAL = ['g', 'gg', 'n', 'd', 'dd', 'r', 'm', 'b', 'bb', 's', 'ss', '', 'j', 'jj', 'ch', 'k', 't', 'p', 'h']
export const ROM_VOWEL = ['a', 'ae', 'ya', 'yae', 'eo', 'e', 'yeo', 'ye', 'o', 'wa', 'wae', 'oe', 'yo', 'u', 'wo', 'we', 'wi', 'yu', 'eu', 'ui', 'i']
export const ROM_FINAL = ['', 'k', 'k', 'k', 'n', 'n', 'n', 't', 'l', 'k', 'm', 'l', 'l', 'l', 'p', 'l', 'm', 'p', 'p', 't', 't', 'ng', 't', 't', 'k', 't', 'p', 't']

export const compose = (L, V, T) => String.fromCharCode(0xAC00 + (L * 21 + V) * 28 + T)
export const isSyllable = (ch) => { const c = String(ch || '').codePointAt(0); return c >= 0xAC00 && c <= 0xD7A3 }

// How the letters sit inside the block — the three classroom layouts.
//   vertical   (ㅏ ㅐ ㅑ ㅒ ㅓ ㅔ ㅕ ㅖ ㅣ): initial on the left, vowel on the right
//   horizontal (ㅗ ㅛ ㅜ ㅠ ㅡ):            initial on top, vowel underneath
//   compound   (ㅘ ㅙ ㅚ ㅝ ㅞ ㅟ ㅢ):       initial top-left, vowel wraps below and right
// A final, when there is one, runs along the bottom in every layout.
const VERTICAL = new Set([0, 1, 2, 3, 4, 5, 6, 7, 20])
const HORIZONTAL = new Set([8, 12, 13, 17, 18])
export const layoutOf = (V) => (VERTICAL.has(V) ? 'vertical' : HORIZONTAL.has(V) ? 'horizontal' : 'compound')

// One syllable → its letters, each with role L (initial) / V (vowel) / T (final).
export function decompose(ch) {
  if (!isSyllable(ch)) return null
  const n = ch.codePointAt(0) - 0xAC00
  const L = Math.floor(n / 588)
  const V = Math.floor((n % 588) / 28)
  const T = n % 28
  const parts = [
    { role: 'L', jamo: INITIALS[L], rom: ROM_INITIAL[L], idx: L },
    { role: 'V', jamo: VOWELS[V], rom: ROM_VOWEL[V], idx: V },
  ]
  if (T) parts.push({ role: 'T', jamo: FINALS[T], rom: ROM_FINAL[T], idx: T })
  return { ch, L, V, T, parts, layout: layoutOf(V) }
}
// A word → syllables; anything that isn't Hangul comes through as a plain
// character with no parts (spaces, punctuation, digits).
export const decomposeWord = (word) => Array.from(String(word || '')).map(ch => decompose(ch) || { ch, parts: [], layout: null, L: -1, V: -1, T: 0 })

// What to SPEAK for one letter on its own. Korean TTS reads a bare jamo as its
// NAME (ㄱ → "기역"), so we voice a minimal syllable instead — the classroom
// "그, 느, 드…" for initials, the vowel behind silent ㅇ, and 으 + the final.
export const soundOf = (part) => {
  if (part.role === 'V') return compose(11, part.idx, 0)                      // 아, 오, 와 …
  if (part.role === 'L') return part.idx === 11 ? '으' : compose(part.idx, 18, 0) // 그, 느 … (ㅇ is silent)
  return compose(11, 18, part.idx)                                            // 윽, 은, 응 …
}
export const partLabel = (part) => `${part.jamo} · ${part.rom || 'silent'}`

// ── lenient scoring ──────────────────────────────────────────────────────────
// Sounds a non-native mouth (and the recogniser) swap freely count as "near":
// half credit, shown in amber, never a miss. The goal is a recognisable word,
// not a perfect one.
const NEAR = [
  ['ㄱ', 'ㅋ', 'ㄲ'], ['ㄷ', 'ㅌ', 'ㄸ'], ['ㅂ', 'ㅍ', 'ㅃ'], ['ㅈ', 'ㅊ', 'ㅉ'], ['ㅅ', 'ㅆ'], ['ㄹ', 'ㄴ'],
  ['ㅐ', 'ㅔ'], ['ㅒ', 'ㅖ'], ['ㅗ', 'ㅓ'], ['ㅜ', 'ㅡ'], ['ㅚ', 'ㅞ', 'ㅙ'], ['ㅝ', 'ㅓ'], ['ㅢ', 'ㅣ'],
]
const near = (a, b) => NEAR.some(g => g.includes(a) && g.includes(b))
export const letterScore = (a, b) => (a === b ? 1 : near(a, b) ? 0.5 : 0)
// Finals by sound class: 값 and 갑 are said the same.
export const finalScore = (ti, tj) => (ti === tj ? 1 : (ti && tj && ROM_FINAL[ti] === ROM_FINAL[tj]) ? 0.9 : (!ti || !tj) ? 0 : (near(FINALS[ti], FINALS[tj]) ? 0.5 : 0))

const W = { L: 1, V: 1, T: 0.7 } // finals are the hardest part for a learner; weigh them less
const partScore = (t, h, role) => {
  if (role === 'L') return letterScore(INITIALS[t.L], INITIALS[h.L])
  if (role === 'V') return letterScore(VOWELS[t.V], VOWELS[h.V])
  return finalScore(t.T, h.T)
}
export const syllableScore = (t, h) => {
  if (!t || !h || !t.parts.length || !h.parts.length) return 0
  let sum = W.L * letterScore(INITIALS[t.L], INITIALS[h.L]) + W.V * letterScore(VOWELS[t.V], VOWELS[h.V])
  let wsum = W.L + W.V
  // A final that was added or dropped counts against — otherwise 학 passes for 사.
  if (t.T || h.T) { sum += W.T * finalScore(t.T, h.T); wsum += W.T }
  return sum / wsum
}
// Below this, a heard syllable isn't "that syllable" at all — sharing one
// vowel by chance shouldn't earn half a mark.
const MIN_SYL = 0.4

// Align the target word with what the recogniser heard (which may be longer,
// shorter, or have a syllable swapped) and score every letter of the target.
// Classic sequence alignment: each target syllable pairs with at most one heard
// syllable, in order; skips cost nothing.
export function alignWord(target, transcript) {
  const T = Array.from(String(target || '')).filter(isSyllable).map(decompose)
  const H = Array.from(String(transcript || '')).filter(isSyllable).map(decompose)
  const n = T.length, m = H.length
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0))
  const back = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(''))
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const s = syllableScore(T[i - 1], H[j - 1])
      const diag = dp[i - 1][j - 1] + (s >= MIN_SYL ? s : 0)
      const up = dp[i - 1][j]
      const left = dp[i][j - 1]
      const best = Math.max(diag, up, left)
      dp[i][j] = best
      back[i][j] = best === diag ? 'd' : best === up ? 'u' : 'l'
    }
  }
  const match = new Array(n).fill(null)
  let i = n, j = m
  while (i > 0 && j > 0) {
    const b = back[i][j]
    if (b === 'd') { if (syllableScore(T[i - 1], H[j - 1]) >= MIN_SYL) match[i - 1] = H[j - 1]; i--; j-- }
    else if (b === 'u') i--
    else j--
  }
  const syllables = T.map((t, k) => {
    const h = match[k]
    const parts = t.parts.map(p => ({ ...p, score: h ? partScore(t, h, p.role) : 0 }))
    return { ...t, heard: h ? h.ch : null, parts, score: h ? syllableScore(t, h) : 0 }
  })
  const total = n ? syllables.reduce((a, s) => a + s.score, 0) / n : 0
  return { syllables, total }
}
